"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toFixed = exports.numberOrUndefined = exports.formatNonNumber = exports.makeClamp = exports.tidyNumber = void 0;
exports.toFixedNumber = toFixedNumber;
/*
 * © 2023 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
const __1 = require("..");
/** Converts a number `n` to a string with `dp` decimal places (defaulting to 4).
 * Replaces non-number values with the default empty state text.
 * This is similar to Manatee's tidyNumber module, but simpler. It lacks the special notation for large numbers using D3.
 * It is also similar to toFixed (in this module), but this always returns the decimal places, while toFixed omits them for integers. */
const tidyNumber = (n, dp = 4) => {
    /** Matches strings representing numbers with an SI multiplier like "24k" or "-1.6μ" */
    const metricSymbolRegex = /^-?[0-9.]+[YZEPTGMkhdcmμunpfazy]$/;
    if (typeof n === "string" && metricSymbolRegex.test(n)) {
        return n;
    }
    // Fascinating JS fact:
    // Number.isFinite returns false for non-number values, while global isFinite coerces to number.
    // So Number.isFinite(null) is false, but isFinite(null) is true.
    if (!n || !isFinite(n))
        return (0, exports.formatNonNumber)(n);
    return Number(n).toFixed(dp);
};
exports.tidyNumber = tidyNumber;
/** Clamp a number between min and max.
 * @returns a clamping function with the specified min and max
 * @example
 * const clamp = makeClamp(5, 10);
 * clamp(12);
 * // returns 10
 */
const makeClamp = (min, max) => (x) => Math.max(Math.min(x, max), min);
exports.makeClamp = makeClamp;
/**
 * Convert non-numeric values to user-friendly strings.
 * @param x 0, ±Infinity, null, undefined, NaN, etc.
 * @returns "0", "∞", "-∞" or the default empty state text
 */
const formatNonNumber = (x) => {
    switch (x) {
        case 0:
            return "0";
        case Infinity:
            return "∞";
        case -Infinity:
            return "-∞";
        default:
            return __1.emptyStateText;
    }
};
exports.formatNonNumber = formatNonNumber;
/** Like Number(x), but all non-number inputs evaluate to undefined, not 0 or NaN.  */
const numberOrUndefined = (x) => {
    if (x === undefined ||
        x === null ||
        typeof x === "boolean" ||
        x === "" ||
        isNaN(Number(x)))
        return undefined;
    else
        return Number(x);
};
exports.numberOrUndefined = numberOrUndefined;
/**
 * @param {number} number - number to test
 * @param {number} decimalPlace - number of decimal places to use
 * @returns {string} Number as string, with the specified number of decimal places, or none if it is an integer.
 */
const toFixed = (number, decimalPlace = 2) => {
    return Number.isFinite(number)
        ? Number.isInteger(number)
            ? number.toString()
            : number.toFixed(decimalPlace)
        : (0, exports.formatNonNumber)(number);
};
exports.toFixed = toFixed;
/** Rounds `number` to `dp` decimal places (default 4) without converting it to a string */
function toFixedNumber(number, dp = 4) {
    const factor = Math.pow(10, dp);
    return Math.round(number * factor) / factor;
}
//# sourceMappingURL=formatting.js.map